'use client';

import { useTranslations } from 'next-intl';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { useGoalsSaveState } from '@/features/project/lib/goals-autosave-coordinator';

interface GoalsCommitButtonProps {
  projectId: string;
  /** Whether the buffer differs from the saved server baseline. */
  isDirty: boolean;
  onCommit: () => void;
}

/**
 * Teal checkmark commit button (Apple-HIG confirm affordance), top-right of the
 * goals editor. Active/teal only when there are unsaved changes; tapping saves
 * the goals — which triggers the server-side LLM evaluation. Replaces the old
 * per-keystroke autosave so the (relatively expensive) evaluation runs on a
 * deliberate commit, not mid-typing.
 */
export function GoalsCommitButton({
  projectId,
  isDirty,
  onCommit,
}: GoalsCommitButtonProps) {
  const t = useTranslations('project');
  const saveState = useGoalsSaveState(projectId);
  const isSaving = saveState.status === 'saving';

  const enabled = isDirty && !isSaving;

  return (
    <button
      type="button"
      onClick={onCommit}
      disabled={!enabled}
      aria-label={t('goalsCommit')}
      className={cn(
        'inline-flex items-center justify-center size-9 rounded-full transition-colors',
        enabled
          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
          : 'bg-muted text-muted-foreground/50 cursor-default'
      )}
    >
      {isSaving ? (
        <Loader2 className="size-5 animate-spin" />
      ) : (
        <Check className="size-5" />
      )}
    </button>
  );
}
